/**
 * MyTargetsWidget — "My Targets" panel for the personal Dashboard.
 *
 * Shows the current rep's own scorecard for the current fiscal quarter:
 * Revenue / Deals / Pipeline / Calls / Emails / Meetings — actual vs target +
 * progress bar + status badge.
 *
 * Renders a tasteful empty-state when no targets are set yet. Visible to all
 * roles — the backend `GET /api/manage/targets/scorecard` (no rep_id) defaults
 * to the caller, so RBAC is automatic.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Target } from 'lucide-react';

function fmtINR(n) {
  if (n == null || n === 0) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
}

const STATUS_STYLES = {
  smashed:  { bar: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'Smashed' },
  on_track: { bar: 'bg-blue-500',    text: 'text-blue-700 dark:text-blue-300',       bg: 'bg-blue-50 dark:bg-blue-900/30',       label: 'On track' },
  behind:   { bar: 'bg-amber-500',   text: 'text-amber-700 dark:text-amber-300',     bg: 'bg-amber-50 dark:bg-amber-900/30',     label: 'Behind' },
  blown:    { bar: 'bg-rose-500',    text: 'text-rose-700 dark:text-rose-300',       bg: 'bg-rose-50 dark:bg-rose-900/30',       label: 'At risk' },
};

function MetricRow({ label, m, isCurrency = false, unit = '' }) {
  const fmt = (v) => isCurrency ? fmtINR(v) : Math.round(v || 0).toLocaleString('en-IN');
  const style = m.status ? STATUS_STYLES[m.status] : null;
  const w = m.pct == null ? 0 : Math.min(100, Math.max(0, m.pct));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-slate-600 dark:text-slate-400">{label}</span>
        <span className="tabular-nums text-slate-700 dark:text-slate-300">
          <span className="font-semibold">{fmt(m.actual)}</span>
          <span className="text-slate-400"> / {fmt(m.target)}{unit}</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
          {m.pct != null && (
            <div className={`h-full ${style?.bar || 'bg-slate-300'} transition-all`} style={{ width: `${w}%` }} />
          )}
        </div>
        {m.pct != null ? (
          <span className={`text-xs font-semibold tabular-nums w-10 text-right ${style?.text || 'text-slate-500'}`}>{Math.round(m.pct)}%</span>
        ) : (
          <span className="text-xs text-slate-400 w-10 text-right italic">—</span>
        )}
      </div>
    </div>
  );
}

export default function MyTargetsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/manage/targets/scorecard`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-5" data-testid="my-targets-widget-loading">
        <div className="h-4 w-24 bg-slate-100 dark:bg-slate-700 rounded animate-pulse mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-6 bg-slate-100 dark:bg-slate-700 rounded animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const overallStatus = data.metrics?.revenue?.status;
  const overallStyle = overallStatus ? STATUS_STYLES[overallStatus] : null;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700" data-testid="my-targets-widget">
      <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-amber-500" />
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">My targets</h3>
          <span className="text-[10px] font-medium text-slate-400 bg-slate-100 dark:bg-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded">{data.period}</span>
        </div>
        {data.has_targets && overallStyle && (
          <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${overallStyle.bg} ${overallStyle.text}`}>
            {overallStyle.label}
          </span>
        )}
      </div>
      <div className="p-5">
        {!data.has_targets ? (
          <div className="text-center py-4">
            <Target className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">No targets set yet</p>
            <p className="text-xs text-slate-500 mt-1">Your manager will set your {data.period} goals soon.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <MetricRow label="Revenue" m={data.metrics.revenue} isCurrency />
            <MetricRow label="Deals won" m={data.metrics.deals} />
            <MetricRow label="Pipeline created" m={data.metrics.pipeline} isCurrency />
            <div className="pt-3 border-t border-slate-100 dark:border-slate-700 space-y-4">
              <MetricRow label="Calls per day" m={data.metrics.calls} />
              <MetricRow label="Emails per day" m={data.metrics.emails} />
              <MetricRow label="Meetings per week" m={data.metrics.meetings} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
